import { FaPlus, FaCheck, FaLock, FaBell, FaFolderOpen } from 'react-icons/fa6';
import ChannelAvatar from '../primitives/ChannelAvatar.jsx';
import SelectTile, { Radio } from '../primitives/SelectTile.jsx';
import YouTubeBadge from '../primitives/YouTubeBadge.jsx';

/* Step 1 — pick the source channel + the upload scope (future vs existing). */
export default function StepChannel({ state, set, connect, channels = [], editing = false }) {
	const ch = state.source;
	// Reauth-required channels are listed (with the avatar's warning badge) but
	// can't be picked as a source until they're reconnected on /channels.
	const selectable = (c) => c.connected !== false;
	const choose = (c) => { if (!editing && selectable(c)) set(d => ({ source: c, name: d.name || `${c.name} auto-dub` })); };
	// When editing, the source channel grid shows only the locked source.
	const shown = editing ? channels.filter(c => c.id === ch?.id) : channels;

	return (
		<div>
			{/* scoped keyframes for the hero's audio-equalizer motif */}
			<style>{`@keyframes adEq{0%,100%{transform:scaleY(.3)}50%{transform:scaleY(1)}}`}</style>

			{/* gradient hero */}
			<div
				className="relative overflow-hidden rounded-3xl mb-7 px-6 py-7 md:px-9 md:py-9"
				style={{ background: 'linear-gradient(120deg,#2a0a4f 0%,#5A0AE0 52%,#8C2CE2 100%)' }}
			>
				{/* decorative blurred blobs */}
				<div className="pointer-events-none absolute -top-16 -right-10 w-56 h-56 rounded-full bg-white/10 blur-2xl" />
				<div className="pointer-events-none absolute -bottom-24 left-1/4 w-56 h-56 rounded-full bg-fuchsia-400/20 blur-3xl" />

				{/* equalizer bars */}
				<div className="pointer-events-none absolute top-7 right-8 hidden sm:flex items-end gap-1 h-10 opacity-80">
					{[0, 1, 2, 3, 4, 5, 6].map(i => (
						<span
							key={i}
							className="w-1.5 h-full rounded-full bg-white/70"
							style={{ transformOrigin: 'bottom', animation: 'adEq 1s ease-in-out infinite', animationDelay: `${i * 0.12}s` }}
						/>
					))}
				</div>

				<div className="relative">
					<span className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-white">
						<YouTubeBadge size={16} className="brightness-0 invert" /> Step 1 · Source
					</span>
					<h2 className="mt-4 text-[26px] md:text-[30px] font-bold text-white tracking-tight leading-tight">Which channel should we dub from?</h2>
					<p className="mt-2 text-[14.5px] text-white/80 leading-relaxed max-w-xl">
						We read your uploads to create dubs. Nothing publishes until you approve it.
					</p>
				</div>
			</div>

			<div className="flex items-center gap-2.5 mb-3">
				<span className="w-7 h-7 rounded-lg bg-primary/10 text-primary grid place-items-center text-[13px] font-bold">1</span>
				<div className="text-[15px] font-semibold text-slate-700">Choose the channel</div>
			</div>
			{editing && (
				<div className="mb-3 text-[12px] text-slate-400 flex items-center gap-1.5"><FaLock size={11} /> The source channel can't be changed after creation.</div>
			)}

			{channels.length === 0 && !editing ? (
				<button
					type="button"
					onClick={() => connect('source')}
					className="w-full rounded-3xl border-[1.5px] border-dashed border-slate-300 hover:border-youtube/50 transition p-9 flex flex-col items-center gap-3.5 bg-gradient-to-b from-slate-50 to-white"
				>
					<div className="w-16 h-16 rounded-2xl grid place-items-center bg-white shadow-medium"><YouTubeBadge size={40} /></div>
					<div className="text-lg font-bold text-slate-800">Connect your YouTube channel</div>
					<div className="text-[12.5px] text-slate-500 text-center max-w-[360px]">Sign in with Google and pick your channel. Takes about 20 seconds.</div>
					<span className="mt-1 inline-flex items-center gap-2 bg-youtube text-white font-semibold text-sm px-5 py-2.5 rounded-xl shadow-sm">
						<YouTubeBadge size={18} className="brightness-0 invert" />Connect with YouTube
					</span>
				</button>
			) : (
				<div className="grid gap-3.5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
					{shown.map(c => {
						const on = ch?.id === c.id;
						const dead = !selectable(c);
						return (
							<button
								key={c.id}
								type="button"
								onClick={() => choose(c)}
								disabled={editing || dead}
								title={dead ? 'Reconnect this channel on the Channels page first' : undefined}
								className={`group relative text-left bg-white rounded-2xl border-[1.5px] overflow-hidden transition ${on ? 'border-primary shadow-[0_0_0_3px_rgba(90,10,224,0.12)]' : 'border-slate-200'} ${!editing && !dead ? 'hover:border-slate-300 hover:shadow-medium hover:-translate-y-0.5' : ''} ${editing || dead ? 'cursor-not-allowed' : ''}`}
							>
								<div className="p-[15px] flex items-center gap-3.5">
									<ChannelAvatar ch={c} size={48} />
									<div className="min-w-0 flex-1">
										<div className="text-[14.5px] font-semibold text-slate-800 truncate">{c.name}</div>
										{dead
											? <div className="text-[12px] font-semibold text-amber-600 mt-0.5 truncate">Reconnect needed · manage on Channels page</div>
											: <div className="text-[12px] text-slate-500 mt-0.5 truncate">{c.handle} · {c.subs} subscribers</div>}
									</div>
									{/* fixed 20px footprint either way → no horizontal shift */}
									{on ? (
										<span className="w-5 h-5 rounded-full bg-primary text-white grid place-items-center flex-none"><FaCheck size={11} /></span>
									) : (
										!dead && <Radio on={false} />
									)}
								</div>

								{/* corner ribbon — absolute, so it never affects card layout (clipped by overflow-hidden) */}
								{on && (
									<span className="pointer-events-none absolute -right-[34px] top-[12px] rotate-45 bg-primary text-white text-[9px] font-bold uppercase tracking-[0.08em] leading-none px-10 py-[5px] shadow-sm">
										Source
									</span>
								)}
							</button>
						);
					})}
					{!editing && (
						<button
							type="button"
							onClick={() => connect('source')}
							className="group rounded-2xl border-[1.5px] border-dashed border-slate-300 hover:border-primary/50 hover:bg-primary/[0.03] transition p-[15px] flex items-center gap-3.5 min-h-[80px]"
						>
							<div className="w-12 h-12 rounded-[13px] border-[1.5px] border-dashed border-slate-300 group-hover:border-primary/50 grid place-items-center text-slate-400 group-hover:text-primary flex-none transition"><FaPlus size={18} /></div>
							<span className="text-sm font-semibold text-slate-500 group-hover:text-primary transition">Connect another channel</span>
						</button>
					)}
				</div>
			)}

			<div className="mt-9">
				<div className="flex items-center gap-2.5 mb-3">
					<span className="w-7 h-7 rounded-lg bg-primary/10 text-primary grid place-items-center text-[13px] font-bold">2</span>
					<div className="text-[15px] font-semibold text-slate-700">Which uploads should we dub?</div>
				</div>
				{editing && (
					<div className="mb-3 text-[12px] text-slate-400 flex items-center gap-1.5"><FaLock size={11} /> Watch vs. backfill can't be changed after creation.</div>
				)}
				<div className="flex gap-3.5 flex-wrap">
					<SelectTile
						selected={state.type === 'watch'} onClick={() => set({ type: 'watch' })} disabled={editing}
						emoji={<FaBell size={20} color="#5A0AE0" />} iconColor="#5A0AE0" badge="Recommended"
						title="Future uploads"
						desc="New videos get dubbed automatically as you publish them. Set it once and leave it running."
					/>
					<SelectTile
						selected={state.type === 'backfill'} onClick={() => set({ type: 'backfill' })} disabled={editing}
						emoji={<FaFolderOpen size={20} color="#8C2CE2" />} iconColor="#8C2CE2"
						title="Existing videos"
						desc="Dub videos already on your channel. You'll pick exactly which ones in the next step."
					/>
				</div>
			</div>
		</div>
	);
}
